<template>
  <v-dialog v-model="internalValue" max-width="1280px" persistent scrollable>
    <v-card class="rounded-xl border-light bg-white">
      <!-- Modal Header -->
      <v-card-title class="d-flex align-center justify-space-between py-3 px-5 border-light-bottom bg-white">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">
            {{ isEdit ? 'mdi-chart-box-outline' : 'mdi-chart-box-plus-outline' }}
          </v-icon>
          <div>
            <span class="text-h6 font-weight-black grey--text text--darken-4">
              {{ isEdit ? `Editar Presupuesto Maestro: ${form.EmbName || ''}` : 'Nuevo Presupuesto Maestro Comercial' }}
            </span>
            <span class="ml-2" v-if="isEdit">
              <v-chip small label outlined color="primary" class="font-weight-bold">
                v{{ form.EmbVersion }}
              </v-chip>
              <v-chip small :color="statusColor" class="font-weight-bold ml-2" text-color="white">
                {{ form.StatusName || 'Borrador' }}
              </v-chip>
            </span>
          </div>
        </div>
        <v-btn icon color="grey darken-2" small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Modal Body -->
      <v-card-text class="pt-4 pb-4 px-5 bg-white" style="max-height: 80vh;">
        <!-- INFORMACIÓN GENERAL (CABECERA) -->
        <v-form ref="form" v-model="valid">
          <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase mb-3">
            <v-icon small color="grey darken-3" class="mr-1">mdi-clipboard-outline</v-icon>
            Información Principal del Presupuesto
          </div>

          <v-row dense class="mb-3">
            <v-col cols="12" md="3">
              <s-select-campaign
                label="Campaña *"
                v-model="form.CmpID"
                clearable
                :error="submitted && !form.CmpID"
                :disabled="isLocked"
              />
            </v-col>
            <v-col cols="12" md="3">
              <s-select-definition
                :def="1175"
                label="División / Destino *"
                v-model="form.TypeDestination"
                clearable
                :error="submitted && !form.TypeDestination"
                :disabled="isLocked"
              />
            </v-col>
            <v-col cols="12" md="4">
              <s-text
                label="Nombre / Descripción del Presupuesto *"
                v-model="form.EmbName"
                :error="submitted && !form.EmbName"
                :disabled="isLocked"
                placeholder="Ej: Presupuesto Maestro Mango Fresco 2026-2027"
              />
            </v-col>
            <v-col cols="12" md="2">
              <s-date
                label="Fecha Base *"
                v-model="form.EmbDate"
                :error="submitted && !form.EmbDate"
                :disabled="isLocked"
              />
            </v-col>

            <v-col cols="12" md="3">
              <s-select-definition
                :def="1129"
                label="Moneda Presupuesto"
                v-model="form.TypeCurrency"
                :disabled="isLocked"
              />
            </v-col>
            <v-col cols="12" md="9">
              <s-text
                label="Observaciones Generales"
                v-model="form.EmbObservation"
                :disabled="isLocked"
                placeholder="Notas de aprobación, premisas comerciales o supuestos..."
              />
            </v-col>
          </v-row>
        </v-form>

        <!-- KPI SUMMARY BAR -->
        <v-card outlined class="pa-3 mb-4 rounded-xl border-light bg-grey-lighten-5" elevation="0">
          <v-row dense align="center">
            <v-col cols="6" md="2" class="text-center">
              <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Clientes Activos</span>
              <div class="text-subtitle-1 font-weight-black grey--text text--darken-4">
                {{ clientRowsCount }} <small class="grey--text font-weight-medium">({{ noClientCount > 0 ? '+ Sin Cliente' : 'Completos' }})</small>
              </div>
            </v-col>
            <v-divider vertical class="my-1 hidden-sm-and-down"></v-divider>

            <v-col cols="6" md="2" class="text-center">
              <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Total Toneladas</span>
              <div class="text-subtitle-1 font-weight-black green--text text--darken-3">
                {{ totalTons.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} TM
              </div>
            </v-col>
            <v-divider vertical class="my-1 hidden-sm-and-down"></v-divider>

            <v-col cols="6" md="2" class="text-center">
              <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Total Contenedores</span>
              <div class="text-subtitle-1 font-weight-black teal--text text--darken-3">
                {{ totalFcl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} FCL
              </div>
            </v-col>
            <v-divider vertical class="my-1 hidden-sm-and-down"></v-divider>

            <v-col cols="6" md="2" class="text-center">
              <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Total Valor FOB</span>
              <div class="text-subtitle-1 font-weight-black blue--text text--darken-3">
                ${{ totalFob.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </div>
            </v-col>
            <v-divider vertical class="my-1 hidden-sm-and-down"></v-divider>

            <!-- PROMEDIOS INFORMATIVOS -->
            <v-col cols="6" md="2" class="text-center">
              <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Prom. TM / FCL</span>
              <div class="text-subtitle-1 font-weight-black orange--text text--darken-3">
                {{ avgTmPerFcl > 0 ? avgTmPerFcl.toFixed(2) + ' TM' : '-' }}
              </div>
            </v-col>
            <v-divider vertical class="my-1 hidden-sm-and-down"></v-divider>

            <v-col cols="6" md="2" class="text-center">
              <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">FOB Promedio USD/kg</span>
              <div class="text-subtitle-1 font-weight-black purple--text text--darken-3">
                {{ avgFobPerKg > 0 ? '$' + avgFobPerKg.toFixed(4) + ' /kg' : '-' }}
              </div>
            </v-col>
          </v-row>
        </v-card>

        <!-- DETALLE DE LÍNEAS / CLIENTES -->
        <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
          <div class="d-flex align-center">
            <v-icon small color="grey darken-3" class="mr-1">mdi-account-group-outline</v-icon>
            <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
              Metas Comerciales por Cliente ({{ activeDetails.length }})
            </span>
          </div>

          <div v-if="!isLocked" class="d-flex align-center flex-wrap">
            <v-btn small color="primary" class="rounded-lg text-none font-weight-bold px-3 mr-2" @click="addClientRow">
              <v-icon left small>mdi-account-plus</v-icon>
              Agregar Cliente
            </v-btn>
            <v-btn
              small
              outlined
              color="amber darken-3"
              class="rounded-lg text-none font-weight-bold px-3 mr-2"
              :disabled="hasNoClientRow"
              @click="addNoClientRow"
            >
              <v-icon left small>mdi-account-question</v-icon>
              + Sin Cliente
            </v-btn>
            <v-btn small outlined color="teal darken-2" class="rounded-lg text-none font-weight-bold px-3 mr-2" @click="showExcelModal = true">
              <v-icon left small>mdi-file-excel</v-icon>
              Pegar de Excel
            </v-btn>
            <v-btn small text color="error" class="rounded-lg text-none font-weight-bold" @click="clearDetails">
              <v-icon left small>mdi-trash-can-outline</v-icon>
              Limpiar
            </v-btn>
          </div>
        </div>

        <v-simple-table dense class="border-rounded border-light elevation-0 detail-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th style="width: 40px;" class="text-center font-weight-bold">#</th>
                <th style="width: 110px;" class="text-center font-weight-bold">Tipo</th>
                <th style="min-width: 250px;" class="text-left font-weight-bold">Cliente Comercial (SAP) *</th>
                <th style="width: 120px;" class="text-right font-weight-bold">TM (Toneladas) *</th>
                <th style="width: 110px;" class="text-right font-weight-bold">FCL (Cont.) *</th>
                <th style="width: 130px;" class="text-right font-weight-bold">FOB USD/kg *</th>
                <th style="width: 140px;" class="text-right font-weight-bold">FOB Total USD</th>
                <th style="width: 95px;" class="text-center font-weight-bold">Ratio TM/FCL</th>
                <th class="text-left font-weight-bold">Observación</th>
                <th v-if="!isLocked" style="width: 90px;" class="text-center font-weight-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="activeDetails.length === 0">
                <td colspan="10" class="text-center py-6 grey--text">
                  <v-icon large color="grey lighten-1">mdi-table-cancel</v-icon>
                  <div class="mt-2 text-caption">No hay metas registradas. Utilice los botones superiores para agregar clientes o pegar desde Excel.</div>
                </td>
              </tr>
              <tr
                v-for="(row, index) in activeDetails"
                :key="index"
                :class="{ 'amber lighten-5': row.EmdIsNoClient }"
              >
                <!-- # -->
                <td class="text-center font-weight-bold text--secondary">
                  {{ index + 1 }}
                </td>

                <!-- Tipo -->
                <td class="text-center">
                  <v-chip x-small label :color="row.EmdIsNoClient ? 'amber darken-3' : 'primary'" text-color="white" class="font-weight-bold">
                    {{ row.EmdIsNoClient ? 'Sin Cliente' : 'Cliente' }}
                  </v-chip>
                </td>

                <!-- Cliente SAP -->
                <td>
                  <template v-if="row.EmdIsNoClient">
                    <s-text
                      v-model="row.EmdCustomerName"
                      placeholder="Ej: Meta Comercial Sin Asignar"
                      :disabled="isLocked"
                    />
                  </template>
                  <template v-else>
                    <sselect-client-sap
                      v-model="row.selectedCustomer"
                      :text="row.EmdCustomerName"
                      return-object
                      clearable
                      :disabled="isLocked"
                      @input="(val) => onCustomerChange(val, row)"
                    />
                  </template>
                </td>

                <!-- TM -->
                <td>
                  <s-text
                    v-model="row.EmdBudgetTons"
                    decimal
                    :disabled="isLocked"
                    @input="recalculateRow(row)"
                  />
                </td>

                <!-- FCL -->
                <td>
                  <s-text
                    v-model="row.EmdBudgetFcl"
                    decimal
                    :disabled="isLocked"
                    @input="recalculateRow(row)"
                  />
                </td>

                <!-- FOB USD/kg -->
                <td>
                  <s-text
                    v-model="row.EmdPriceFobKg"
                    decimal
                    :disabled="isLocked"
                    placeholder="0.000000"
                    @input="recalculateRow(row)"
                  />
                </td>

                <!-- FOB Total USD (calculado, no editable) -->
                <td class="text-right font-weight-bold blue--text text--darken-3">
                  ${{ Number(row.EmdBudgetFobUsd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                </td>

                <!-- Ratio Fila TM/FCL -->
                <td class="text-center text--secondary font-weight-bold">
                  {{ (row.EmdBudgetFcl > 0 && row.EmdBudgetTons > 0) ? (row.EmdBudgetTons / row.EmdBudgetFcl).toFixed(2) : '-' }}
                </td>

                <!-- Obs -->
                <td>
                  <s-text
                    v-model="row.EmdObservation"
                    placeholder="Notas o condición..."
                    :disabled="isLocked"
                  />
                </td>

                <!-- Acciones -->
                <td v-if="!isLocked" class="text-center">
                  <v-btn icon small color="primary" title="Duplicar línea" @click="duplicateRow(row)">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                  <v-btn icon small color="error" title="Eliminar línea" @click="removeRow(row)">
                    <v-icon small>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="activeDetails.length > 0">
              <tr class="bg-grey-lighten-5 font-weight-bold">
                <td colspan="3" class="text-right font-weight-black grey--text text--darken-4">TOTAL GENERAL:</td>
                <td class="text-right font-weight-black green--text text--darken-3">
                  {{ totalTons.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                </td>
                <td class="text-right font-weight-black teal--text text--darken-3">
                  {{ totalFcl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                </td>
                <td class="text-right font-weight-black purple--text text--darken-3">
                  {{ avgFobPerKg > 0 ? '$' + avgFobPerKg.toFixed(4) : '-' }}
                </td>
                <td class="text-right font-weight-black blue--text text--darken-3">
                  ${{ totalFob.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                </td>
                <td class="text-center font-weight-black orange--text text--darken-3">
                  {{ avgTmPerFcl > 0 ? avgTmPerFcl.toFixed(2) : '-' }}
                </td>
                <td :colspan="isLocked ? 1 : 2"></td>
              </tr>
            </tfoot>
          </template>
        </v-simple-table>
      </v-card-text>

      <!-- Modal Footer -->
      <v-card-actions class="px-5 pb-4 pt-3 border-light-top bg-white d-flex justify-space-between">
        <div>
          <v-btn
            v-if="isEdit && !isLocked"
            color="success"
            class="rounded-lg px-4 font-weight-bold text-none"
            :loading="locking"
            @click="confirmLock"
          >
            <v-icon left small>mdi-lock-check</v-icon>
            Aprobar y Bloquear Presupuesto
          </v-btn>
        </div>

        <div class="d-flex align-center">
          <v-btn text class="rounded-lg px-4 text-none font-weight-bold mr-2" @click="close">
            Cerrar
          </v-btn>
          <v-btn
            v-if="!isLocked"
            color="primary"
            class="rounded-lg px-5 font-weight-bold text-none"
            :loading="saving"
            @click="save"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Guardar Presupuesto
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>

    <!-- Modal Pegar de Excel -->
    <paste-excel-budget-modal
      v-if="showExcelModal"
      v-model="showExcelModal"
      @imported="onExcelImported"
    />
  </v-dialog>
</template>

<script>
import CommercialMasterBudgetService from "@/services/ExternalTrade/CommercialMasterBudgetService";
import PasteExcelBudgetModal from "./PasteExcelBudgetModal.vue";
import SselectClientSap from "@/components/Sale/SselectClientSap.vue";

export default {
  name: "CommercialMasterBudgetFormModal",
  components: {
    PasteExcelBudgetModal,
    SselectClientSap,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    budgetId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      valid: false,
      saving: false,
      locking: false,
      loadingBudget: false,
      submitted: false,
      showExcelModal: false,
      form: {
        EmbID: 0,
        TypeDestination: null,
        CmpID: null,
        EmbName: "",
        EmbVersion: 1,
        EmbDate: new Date().toISOString().substr(0, 10),
        TypeCurrency: 2, // USD
        EmbObservation: "",
        EmbStatus: 1, // 1: Borrador
        EmbParentID: null,
        EmbIsCurrent: true,
        SecStatus: 1,
        Details: [],
      },
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isEdit() {
      return this.form.EmbID > 0;
    },
    isLocked() {
      return this.form.EmbStatus === 2 || this.form.EmbStatus === 3;
    },
    statusColor() {
      if (this.form.EmbStatus === 2) return "success";
      if (this.form.EmbStatus === 3) return "error";
      return "info";
    },
    activeDetails() {
      return this.form.Details.filter((d) => d.SecStatus !== 0);
    },
    clientRowsCount() {
      return this.activeDetails.filter((d) => !d.EmdIsNoClient).length;
    },
    noClientCount() {
      return this.activeDetails.filter((d) => d.EmdIsNoClient).length;
    },
    hasNoClientRow() {
      return this.noClientCount > 0;
    },
    totalTons() {
      return this.activeDetails.reduce((a, b) => a + (Number(b.EmdBudgetTons) || 0), 0);
    },
    totalFcl() {
      return this.activeDetails.reduce((a, b) => a + (Number(b.EmdBudgetFcl) || 0), 0);
    },
    totalFob() {
      return this.activeDetails.reduce((a, b) => a + (Number(b.EmdBudgetFobUsd) || 0), 0);
    },
    avgTmPerFcl() {
      return this.totalFcl > 0 ? this.totalTons / this.totalFcl : 0;
    },
    avgFobPerKg() {
      return this.totalTons > 0 ? this.totalFob / (this.totalTons * 1000) : 0;
    },
  },
  created() {
    this.initForm();
  },
  watch: {
    value(val) {
      if (val && !this.loadingBudget) {
        this.submitted = false;
        this.initForm();
      }
    },
    budgetId(newId, oldId) {
      if (this.value && newId !== oldId && !this.loadingBudget) {
        this.initForm();
      }
    },
  },
  methods: {
    initForm() {
      if (this.budgetId && this.budgetId > 0) {
        this.loadBudget(this.budgetId);
      } else {
        this.form = {
          EmbID: 0,
          TypeDestination: null,
          CmpID: null,
          EmbName: "",
          EmbVersion: 1,
          EmbDate: new Date().toISOString().substr(0, 10),
          TypeCurrency: 2,
          EmbObservation: "",
          EmbStatus: 1,
          EmbParentID: null,
          EmbIsCurrent: true,
          SecStatus: 1,
          Details: [],
        };
      }
    },
    loadBudget(embID) {
      if (!embID || embID <= 0) return;
      this.loadingBudget = true;
      CommercialMasterBudgetService.getById(embID, this.$fun.getUserID())
        .then((res) => {
          const data = res.data;
          this.form = {
            EmbID: data.EmbID,
            TypeDestination: data.TypeDestination,
            CmpID: data.CmpID,
            EmbName: data.EmbName,
            EmbVersion: data.EmbVersion,
            EmbDate: data.EmbDate ? data.EmbDate.substr(0, 10) : new Date().toISOString().substr(0, 10),
            TypeCurrency: data.TypeCurrency || 2,
            EmbObservation: data.EmbObservation || "",
            EmbStatus: data.EmbStatus,
            StatusName: data.StatusName,
            EmbParentID: data.EmbParentID,
            EmbIsCurrent: data.EmbIsCurrent,
            SecStatus: data.SecStatus !== undefined && data.SecStatus !== 0 ? data.SecStatus : 1,
            Details: (data.Details || []).map((d) => ({
              ...d,
              EmdPriceFobKg: d.EmdPriceFobKg !== undefined && d.EmdPriceFobKg !== null ? d.EmdPriceFobKg : (d.EmdBudgetTons > 0 ? Number((d.EmdBudgetFobUsd / (d.EmdBudgetTons * 1000)).toFixed(6)) : 0),
              EmdCustomerName: d.EmdCustomerName || "",
              PrsCustomerID: d.PrsCustomerID || null,
              selectedCustomer: d.PrsCustomerID
                ? {
                    CardCode: d.PrsCustomerID,
                    CardName: d.EmdCustomerName || d.PrsCustomerID,
                    PrsCustomerID: d.PrsCustomerID,
                    PrsName: d.EmdCustomerName || d.PrsCustomerID,
                  }
                : null,
            })),
          };
        })
        .catch((err) => {
          this.$fun?.alert("Error al cargar presupuesto: " + (err.response?.data?.Message || err.message), "error");
        })
        .finally(() => {
          this.loadingBudget = false;
        });
    },
    close() {
      this.internalValue = false;
    },
    addClientRow() {
      this.form.Details.push({
        EmdID: 0,
        EmbID: this.form.EmbID || 0,
        EmdIsNoClient: false,
        PrsCustomerID: null,
        EmdCustomerName: "",
        selectedCustomer: null,
        EmdBudgetTons: 0,
        EmdBudgetFcl: 0,
        EmdPriceFobKg: 0,
        EmdBudgetFobUsd: 0,
        EmdObservation: "",
        SecStatus: 1,
      });
    },
    addNoClientRow() {
      if (this.hasNoClientRow) {
        this.$fun?.alert("Ya existe una línea 'Sin Cliente'. Solo se permite una línea global sin asignar.", "warning");
        return;
      }
      this.form.Details.push({
        EmdID: 0,
        EmbID: this.form.EmbID || 0,
        EmdIsNoClient: true,
        PrsCustomerID: null,
        EmdCustomerName: "SIN CLIENTE ASIGNADO",
        selectedCustomer: null,
        EmdBudgetTons: 0,
        EmdBudgetFcl: 0,
        EmdPriceFobKg: 0,
        EmdBudgetFobUsd: 0,
        EmdObservation: "Meta comercial pendiente de asignación",
        SecStatus: 1,
      });
    },
    duplicateRow(row) {
      this.form.Details.push({
        EmdID: 0,
        EmbID: this.form.EmbID || 0,
        EmdIsNoClient: row.EmdIsNoClient,
        PrsCustomerID: row.EmdIsNoClient ? null : row.PrsCustomerID,
        EmdCustomerName: row.EmdCustomerName,
        selectedCustomer: row.selectedCustomer ? { ...row.selectedCustomer } : null,
        EmdBudgetTons: row.EmdBudgetTons,
        EmdBudgetFcl: row.EmdBudgetFcl,
        EmdPriceFobKg: row.EmdPriceFobKg,
        EmdBudgetFobUsd: row.EmdBudgetFobUsd,
        EmdObservation: row.EmdObservation,
        SecStatus: 1,
      });
      this.$fun?.alert("Línea duplicada correctamente.", "success");
    },
    removeRow(row) {
      if (row.EmdID && row.EmdID > 0) {
        row.SecStatus = 0;
      } else {
        const index = this.form.Details.indexOf(row);
        if (index > -1) {
          this.form.Details.splice(index, 1);
        }
      }
    },
    clearDetails() {
      this.form.Details.forEach((d) => {
        if (d.EmdID > 0) d.SecStatus = 0;
      });
      this.form.Details = this.form.Details.filter((d) => d.EmdID > 0);
    },
    onCustomerChange(customerObj, row) {
      if (customerObj && typeof customerObj === "object") {
        row.PrsCustomerID = customerObj.CardCode || customerObj.PrsCustomerID || customerObj.ClientID || customerObj.id || null;
        row.EmdCustomerName = customerObj.CardName || customerObj.PrsName || customerObj.PrsCustomerName || customerObj.text || (row.PrsCustomerID ? String(row.PrsCustomerID) : "");
      } else if (typeof customerObj === "string" && customerObj.trim().length > 0) {
        row.PrsCustomerID = customerObj.trim();
        if (!row.EmdCustomerName) {
          row.EmdCustomerName = customerObj.trim();
        }
      } else {
        row.PrsCustomerID = null;
        row.EmdCustomerName = "";
      }
    },
    recalculateRow(row) {
      if (row.EmdBudgetTons < 0) row.EmdBudgetTons = 0;
      if (row.EmdBudgetFcl < 0) row.EmdBudgetFcl = 0;
      if (row.EmdPriceFobKg < 0) row.EmdPriceFobKg = 0;
      const tons = Number(row.EmdBudgetTons) || 0;
      const priceKg = Number(row.EmdPriceFobKg) || 0;
      row.EmdBudgetFobUsd = Number((tons * 1000 * priceKg).toFixed(2));
    },
    onExcelImported(importedRows) {
      importedRows.forEach((r) => {
        const tons = Number(r.EmdBudgetTons) || 0;
        const priceKg = Number(r.EmdPriceFobKg) || 0;
        const fobUsd = r.EmdBudgetFobUsd && r.EmdBudgetFobUsd > 0 ? Number(r.EmdBudgetFobUsd) : Number((tons * 1000 * priceKg).toFixed(2));
        this.form.Details.push({
          ...r,
          EmbID: this.form.EmbID || 0,
          EmdBudgetTons: tons,
          EmdPriceFobKg: priceKg,
          EmdBudgetFobUsd: fobUsd,
        });
      });
      this.$fun?.alert(`${importedRows.length} líneas importadas correctamente.`, "success");
    },
    validateBudget() {
      this.submitted = true;

      if (!this.form.CmpID || !this.form.TypeDestination || !this.form.EmbName || !this.form.EmbDate) {
        this.$fun?.alert("Complete los campos obligatorios de la cabecera.", "warning");
        return false;
      }

      const active = this.activeDetails;
      if (active.length === 0) {
        this.$fun?.alert("Debe ingresar al menos una línea de cliente en el presupuesto.", "error");
        return false;
      }

      // Sincronizar clientes seleccionados antes de validar
      active.forEach((row) => {
        if (!row.EmdIsNoClient && row.selectedCustomer) {
          this.onCustomerChange(row.selectedCustomer, row);
        }
      });

      const seenClients = new Set();
      let noClientCount = 0;

      for (let i = 0; i < active.length; i++) {
        const row = active[i];
        if (row.EmdIsNoClient) {
          noClientCount++;
        } else {
          if (!row.PrsCustomerID) {
            this.$fun?.alert(`Fila #${i + 1}: Debe seleccionar un Cliente SAP válido.`, "error");
            return false;
          }
          if (seenClients.has(row.PrsCustomerID)) {
            this.$fun?.alert(`El cliente ${row.EmdCustomerName || row.PrsCustomerID} está duplicado en el presupuesto.`, "error");
            return false;
          }
          seenClients.add(row.PrsCustomerID);
        }

        const tons = Number(row.EmdBudgetTons) || 0;
        const fcl = Number(row.EmdBudgetFcl) || 0;
        const priceKg = Number(row.EmdPriceFobKg) || 0;

        if (tons <= 0) {
          this.$fun?.alert(`Fila #${i + 1} (${row.EmdCustomerName || 'Sin Cliente'}): Las toneladas (TM) deben ser mayores a 0.`, "error");
          return false;
        }
        if (priceKg <= 0) {
          this.$fun?.alert(`Fila #${i + 1} (${row.EmdCustomerName || 'Sin Cliente'}): El precio FOB USD/kg es obligatorio y debe ser mayor que cero.`, "error");
          return false;
        }
        if (fcl < 0) {
          this.$fun?.alert(`Fila #${i + 1} (${row.EmdCustomerName || 'Sin Cliente'}): Los contenedores (FCL) no pueden ser negativos.`, "error");
          return false;
        }
      }

      if (noClientCount > 1) {
        this.$fun?.alert("Solo se permite un registro con la condición 'Sin Cliente'.", "error");
        return false;
      }

      return true;
    },
    save() {
      if (!this.validateBudget()) return;

      this.saving = true;
      const payload = {
        EmbID: this.form.EmbID,
        TypeDestination: this.form.TypeDestination,
        CmpID: this.form.CmpID,
        EmbName: this.form.EmbName,
        EmbVersion: this.form.EmbVersion || 1,
        EmbDate: this.form.EmbDate,
        TypeCurrency: this.form.TypeCurrency || 2,
        EmbObservation: this.form.EmbObservation,
        EmbStatus: this.form.EmbStatus || 1,
        EmbParentID: this.form.EmbParentID,
        EmbIsCurrent: this.form.EmbIsCurrent !== undefined ? this.form.EmbIsCurrent : true,
        SecStatus: this.form.SecStatus !== undefined && this.form.SecStatus !== 0 ? this.form.SecStatus : 1,
        Details: this.form.Details.map((d) => {
          const tons = Number(d.EmdBudgetTons) || 0;
          const priceKg = Number(d.EmdPriceFobKg) || 0;
          const fobUsd = Number((tons * 1000 * priceKg).toFixed(2));
          return {
            EmdID: d.EmdID || 0,
            EmbID: this.form.EmbID || 0,
            EmdIsNoClient: d.EmdIsNoClient || false,
            PrsCustomerID: d.EmdIsNoClient ? null : d.PrsCustomerID,
            EmdCustomerName: d.EmdCustomerName,
            EmdBudgetTons: tons,
            EmdBudgetFcl: Number(d.EmdBudgetFcl) || 0,
            EmdPriceFobKg: priceKg,
            EmdBudgetFobUsd: fobUsd,
            EmdObservation: d.EmdObservation || "",
            SecStatus: d.SecStatus !== undefined ? d.SecStatus : 1,
          };
        }),
      };

      CommercialMasterBudgetService.save(payload, this.$fun.getUserID())
        .then(() => {
          this.$fun?.alert("Presupuesto Maestro guardado con éxito.", "success");
          this.saving = false;
          this.$emit("saved");
          this.close();
        })
        .catch((err) => {
          this.saving = false;
          this.$fun?.alert("Error al guardar presupuesto: " + (err.response?.data?.Message || err.message), "error");
        });
    },
    confirmLock() {
      this.$fun
        ?.alert("¿Está seguro de Aprobar y Bloquear este Presupuesto Maestro? Una vez bloqueado, servirá como línea base fija y solo podrá modificarse creando una nueva versión.", "question")
        .then((r) => {
          if (r && r.value) {
            this.locking = true;
            CommercialMasterBudgetService.lock(this.form.EmbID, this.$fun.getUserID())
              .then(() => {
                this.$fun?.alert("Presupuesto Maestro Aprobado y Bloqueado.", "success");
                this.locking = false;
                this.$emit("saved");
                this.close();
              })
              .catch((err) => {
                this.locking = false;
                this.$fun?.alert("Error al bloquear: " + (err.response?.data?.Message || err.message), "error");
              });
          }
        });
    },
  },
};
</script>

<style scoped>
.border-light {
  border: 1px solid #e2e8f0 !important;
}

.border-light-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.border-light-top {
  border-top: 1px solid #e2e8f0 !important;
}

.bg-grey-lighten-5 {
  background-color: #f8fafc !important;
}

.border-rounded {
  border-radius: 8px;
  overflow: hidden;
}

.detail-table th {
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: #f8fafc !important;
  color: #334155 !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.detail-table td {
  border-bottom: 1px solid #f1f5f9 !important;
}
</style>
